import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { services, serviceCategories } from '@/mocks/services';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location]);

  const scrollToSection = (id: string) => {
    if (location.pathname !== '/') {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  const isHome = location.pathname === '/';

  const navLinks = [
    { label: 'Home', to: '/' },
    { label: 'Gallery', action: 'gallery' },
    { label: 'About Us', action: 'about' },
    { label: 'Contact', action: 'contact' },
  ];

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-brand-teal shadow-lg shadow-black/20"
    >
      <div className="px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <img
              src="https://storage.readdy-site.link/project_files/46b5028d-8e1d-4b10-b368-77259c0a6540/f47a4515-632d-48b9-9db8-560f30e0850b_VALLEY-VIEW-CONSTRUCTION-LLC_logo_white.png?v=d3d0dec20be7cdc5ea07dc6e0ae5efd5"
              alt="Valley View Construction Logo"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium text-white/90 hover:text-brand-teal-light transition-colors duration-200 whitespace-nowrap"
            >
              Home
            </Link>

            {/* Services Mega Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white/90 hover:text-brand-teal-light transition-colors duration-200 whitespace-nowrap cursor-pointer"
                onMouseEnter={() => setServicesOpen(true)}
                onClick={() => setServicesOpen(!servicesOpen)}
              >
                Services
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>

              {servicesOpen && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[780px] bg-brand-dark border border-brand-teal/20 rounded-xl overflow-hidden"
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <div className="p-6">
                    <div className="grid grid-cols-4 gap-6">
                      {serviceCategories.map((cat) => (
                        <div key={cat.name}>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-6 h-6 flex items-center justify-center">
                              <i className={`${cat.icon} text-brand-teal text-sm`} />
                            </div>
                            <span className="text-xs font-semibold text-brand-teal uppercase tracking-wider">
                              {cat.name}
                            </span>
                          </div>
                          <ul className="space-y-1">
                            {cat.services.map((sid) => {
                              const svc = services.find((s) => s.id === sid);
                              if (!svc) return null;
                              return (
                                <li key={sid}>
                                  <Link
                                    to={`/services/${sid}`}
                                    className="flex items-center gap-2 py-1.5 px-2 rounded-lg text-sm text-white/75 hover:text-white hover:bg-brand-teal/10 transition-all duration-150 group"
                                    onClick={() => setServicesOpen(false)}
                                  >
                                    <i className={`${svc.icon} text-white/40 group-hover:text-brand-teal-light text-sm transition-colors`} />
                                    <span>{svc.name}</span>
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 pt-4 border-t border-brand-teal/15 flex items-center justify-between">
                      <p className="text-xs text-white/50">
                        Serving Michiana within 30 miles of Elkhart, IN
                      </p>
                      <Link
                        to="/services"
                        className="text-xs font-semibold text-brand-teal-light hover:text-white transition-colors flex items-center gap-1"
                        onClick={() => setServicesOpen(false)}
                      >
                        View All Services <i className="ri-arrow-right-line" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => scrollToSection('gallery')}
              className="px-4 py-2 text-sm font-medium text-white/90 hover:text-brand-teal-light transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Gallery
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="px-4 py-2 text-sm font-medium text-white/90 hover:text-brand-teal-light transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              About Us
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="px-4 py-2 text-sm font-medium text-white/90 hover:text-brand-teal-light transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Contact
            </button>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+15744096118"
              className="flex items-center gap-2 text-white/90 hover:text-brand-teal-light transition-colors"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-brand-teal-light" />
              </div>
              <span className="text-sm font-semibold whitespace-nowrap">574-409-6118</span>
            </a>
            <button
              onClick={() => scrollToSection('contact')}
              className="px-5 py-2.5 bg-brand-teal hover:bg-brand-teal-light text-white text-sm font-semibold rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </button>
          </div>

          {/* Mobile Toggle */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-brand-teal-dark border-t border-white/20 px-4 py-4 space-y-1">
          <Link
            to="/"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/90 hover:bg-brand-teal/10 hover:text-brand-teal-light transition-all"
          >
            <i className="ri-home-line" />
            <span className="font-medium">Home</span>
          </Link>

          <div>
            <button
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-white/90 hover:bg-brand-teal/10 hover:text-brand-teal-light transition-all cursor-pointer"
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
            >
              <div className="flex items-center gap-3">
                <i className="ri-tools-line" />
                <span className="font-medium">Services</span>
              </div>
              <i className={`ri-arrow-down-s-line transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileServicesOpen && (
              <div className="pl-4 space-y-1 mt-1">
                {serviceCategories.map((cat) =>
                  cat.services.map((sid) => {
                    const svc = services.find((s) => s.id === sid);
                    if (!svc) return null;
                    return (
                      <Link
                        key={sid}
                        to={`/services/${sid}`}
                        className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-white/75 hover:bg-brand-teal/10 hover:text-brand-teal-light transition-all text-sm"
                      >
                        <i className={`${svc.icon} text-brand-teal/70`} />
                        <span>{svc.name}</span>
                      </Link>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {[
            { label: 'Gallery', id: 'gallery', icon: 'ri-image-line' },
            { label: 'About Us', id: 'about', icon: 'ri-information-line' },
            { label: 'Contact', id: 'contact', icon: 'ri-mail-line' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/90 hover:bg-brand-teal/10 hover:text-brand-teal-light transition-all cursor-pointer"
            >
              <i className={item.icon} />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}

          <div className="pt-4 border-t border-brand-teal/15 space-y-3">
            <a
              href="tel:+15744096118"
              className="flex items-center gap-3 px-4 py-3 rounded-lg bg-brand-teal/10 text-white font-semibold"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-brand-teal-light" />
              </div>
              574-409-6118
            </a>
            <button
              onClick={() => scrollToSection('contact')}
              className="w-full py-3 bg-brand-teal text-white font-semibold rounded-lg hover:bg-brand-teal-light transition-colors cursor-pointer"
            >
              Get Free Quote
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
